import { analytics, trackEvent } from "./services/analytics";
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "./components/ErrorFallBack";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import RouteList from "./features/home/RouteList";
import PricePage from "./features/prices/PricePage";
import Printing from "./features/login/Printing";
import ListDoc from "./features/documents/ListDoc";
import BlankSheet from "./features/documents/BlankSheet";
import PaymentPage from "./features/payment/PaymentPage";
import VerifyUser from "./features/login/VerifyUser";
import AdvertisementPage from "./features/ads/AdvertisementPage";
import AdsSync from "./features/ads/AdsSync";
import TopUsersPage from "./features/top-users/TopUsersPage";
import NetworkStatus from "./components/modals/NetworkStatus";
import InactivityModal from "./components/modals/InactivityModal";
import ApparatNotWork from "./components/alerts/ApparatNotWork";
import PrinterErrorBanner from "./components/alerts/PrinterErrorBanner";
import EditManyDocs from "./features/print-settings/EditManyDocs";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ServicePanel from "./features/service/ServicePanel";
import ServicePrintSent from "./features/service/ServicePrintSent";
import {
    clearServiceSession,
    getServiceToken,
    heartbeatServiceSession,
} from "./services/kioskServiceSession";

// The old single-document screen was replaced by the same editor used for
// several documents; keep old links (payment "back", bookmarks) working.
function SingleDocRedirect() {
    const { id } = useParams();
    return <Navigate to={`/printing/edit_many_docs?ids_docs=${encodeURIComponent(id)}`} replace />;
}

function NotFound() {
    const { t } = useTranslation();
    return (
        <div className="flex justify-center items-center h-[88vh] text-[2.4vh] font-bold text-[#7B8290]">
            {t("header.not_found")}
        </div>
    );
}


function App() {
    const location = useLocation();
    useEffect(() => { analytics.pageView(location.pathname); }, [location.pathname]);
    const isHomePage = location.pathname === "/";
    const isNetworkCheckNeeded = !isHomePage;
    const [isLocked, setIsLocked] = useState(false);
    const [lockMessage, setLockMessage] = useState("");
    const [lang, setLang] = useState("ru");

    // Загрузка состояния из LocalStorage
    useEffect(() => {
        const storedState = localStorage.getItem("lockState");
        if (storedState) {
            try {
                const parsedState = JSON.parse(storedState);
                if (parsedState && typeof parsedState === 'object') {
                    setIsLocked(Boolean(parsedState.isLocked));
                    setLockMessage(typeof parsedState.lockMessage === 'string' ? parsedState.lockMessage : "");
                    setLang(['ru', 'kk', 'en'].includes(parsedState.lang) ? parsedState.lang : 'ru');
                }
            } catch {
                // A damaged saved value must not crash every subsequent boot.
                localStorage.removeItem('lockState');
            }
        }
    }, []);

    // Sliding TTL сервисного режима: продлеваем только по реальной активности,
    // а не вечным таймером. Даже если UI зависнет на открытой странице, Redis
    // сам закроет сервисные права через пять минут.
    useEffect(() => {
        if (!getServiceToken()) return;
        let lastHeartbeat = 0;
        const heartbeat = async () => {
            const now = Date.now();
            if (now - lastHeartbeat < 60 * 1000) return;
            lastHeartbeat = now;
            try {
                await heartbeatServiceSession();
            } catch {
                clearServiceSession();
            }
        };
        void heartbeat();
        const events = ["touchstart", "mousemove", "keydown"];
        events.forEach((event) => window.addEventListener(event, heartbeat));
        return () => events.forEach((event) => window.removeEventListener(event, heartbeat));
    }, [location.pathname]);

    // Сохранение состояния
    useEffect(() => {
        const stateToSave = { isLocked, lockMessage, lang };
        localStorage.setItem("lockState", JSON.stringify(stateToSave));
    }, [isLocked, lockMessage, lang]);

    // Слушаем lock-screen от Electron.
    // onLockScreen сам возвращает функцию отписки (в preload.js нет отдельного
    // offLockScreen) — раньше это значение отбрасывалось и подписка никогда не снималась.
    useEffect(() => {
        if (!window.electronAPI) return;
        const handler = (event, data) => {
            setIsLocked(data.status === "locked");
            setLockMessage(data.message || "");
            if (data.lang) setLang(data.lang);
        };
        const unsubscribe = window.electronAPI.onLockScreen(handler);
        return () => unsubscribe?.();
    }, []);

    // Слушаем refresh-page
    useEffect(() => {
        if (!window.electronAPI) return;
        const handler = () => {
            console.log("Получен сигнал refreshed - перезагружаем страницу");
            window.location.reload();
        };
        window.electronAPI.onRefreshPage(handler);
        return () => {
            if (window.electronAPI.offRefreshPage) {
                window.electronAPI.offRefreshPage(handler);
            }
        };
    }, []);

    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={(error, info) => {
                trackEvent("ui_error", { status: "boundary", source: "ui" });
                console.error("Глобальная ошибка:", error, info);
            }}
            onReset={() => {
                localStorage.setItem("reloadCount", "0");
            }}
        >
            <div data-kiosk-ready="true" className="min-h-screen overflow-hidden">
                <AdsSync />
                <Header />
                <main>
                    <ToastContainer position="top-right" autoClose={3000} />
                    {isNetworkCheckNeeded && <NetworkStatus />}
                    <Routes>
                        <Route path="/" element={<RouteList />} />
                        <Route path="/prices" element={<PricePage />} />
                        <Route path="/printing" element={<Printing />} />
                        <Route path="/printing/verify" element={<VerifyUser />} />
                        <Route path="/printing/show_list/user" element={<ListDoc />} />
                        <Route path="/printing/blank_sheet" element={<BlankSheet />} />
                        <Route path="/printing/edit_many_docs" element={<EditManyDocs />} />
                        <Route path="/printing/show_list/:id" element={<SingleDocRedirect />} />
                        <Route path="/payments" element={<PaymentPage />} />
                        <Route path="/advertisement" element={<AdvertisementPage />} />
                        <Route path="/top_users" element={<TopUsersPage />} />
                        <Route path="/service" element={<ServicePanel />} />
                        <Route path="/service/print-sent" element={<ServicePrintSent />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </main>
                <Footer />
                {!isHomePage && <InactivityModal />}
                <ApparatNotWork
                    isOpen={isLocked}
                    lang={lang}
                    message={lockMessage}
                    autoSwitchLang={true}
                />
                <PrinterErrorBanner />
            </div>
        </ErrorBoundary>
    );
}

export default App;
